<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="8">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? `Vendor quotation ${obj.orderNo || ""}` : "Record vendor quotation" }}
              <CBadge
                v-if="obj.id && obj.status"
                :color="badgeColor('purchaseQuotation', obj.status)"
                class="ml-2 align-middle"
              >
                {{ badgeLabel("purchaseQuotation", obj.status) }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{ obj.id ? "Edit the details of this quotation from your vendor" : "Record a quotation you received from a vendor" }}
            </p>
          </CCol>
          <CCol md="4" class="d-flex justify-content-md-end align-items-center mt-3 mt-md-0">
            <CButton
              v-if="obj.id"
              color="primary"
              variant="outline"
              @click="createPurchaseOrder"
            >
              <CIcon name="cil-cart" class="mr-1" /> Create purchase order
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CForm novalidate @submit.prevent="submit">
      <!-- Basic Information -->
      <CCard class="mb-4 border-0">
        <CCardHeader class="bg-transparent border-0 p-4">
          <h2 class="h6 font-weight-bold mb-0">
            <CIcon name="cil-description" class="mr-2" />
            Vendor quotation details
          </h2>
        </CCardHeader>
        <CCardBody class="p-4">
          <CRow form>
            <CCol md="6">
              <CInput
                ref="orderNoField"
                label="Quotation reference *"
                v-model="obj.orderNo"
                placeholder="e.g. Q-2026-0042"
                :is-valid="submitted && !obj.orderNo ? false : null"
                invalid-feedback="Enter the reference number shown on the vendor's quotation"
              />
            </CCol>
            <CCol md="6">
              <CInput
                ref="dateField"
                label="Quotation date *"
                type="date"
                :value="computeDate"
                @change="setDate"
                :is-valid="submitted && !computeDate ? false : null"
                invalid-feedback="Choose the date on the quotation"
              />
            </CCol>
          </CRow>
          <CRow form>
            <CCol md="6">
              <div class="form-group" ref="vendorField">
                <label for="pq-vendor">Vendor *</label>
                <v-select
                  inputId="pq-vendor"
                  style="width: 100%"
                  v-model="selectedSupplier"
                  :label="'name'"
                  :options="supplierItemsWithAddNew"
                  placeholder="Select vendor"
                  @input="handleSupplierSelect"
                  :class="{ 'is-invalid': submitted && !obj.vendorId }"
                >
                  <span slot="no-options" @click="addNewSupplier">
                    Add new vendor
                  </span>
                </v-select>
                <div v-if="submitted && !obj.vendorId" class="invalid-feedback d-block">
                  Choose a vendor, or add a new one
                </div>
              </div>
            </CCol>
            <CCol md="6">
              <CInput
                label="Valid until (optional)"
                type="date"
                v-model="obj.validUntil"
              />
            </CCol>
          </CRow>
          <CRow form>
            <CCol md="12">
              <CTextarea
                label="Description or notes (optional)"
                v-model="obj.description"
                rows="3"
                placeholder="Terms, delivery details or anything worth remembering"
              />
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <!-- Documents -->
      <CCard class="mb-4 border-0">
        <CCardHeader class="bg-transparent border-0 p-4">
          <h2 class="h6 font-weight-bold mb-0">
            <CIcon name="cil-paperclip" class="mr-2" />
            Supporting document
          </h2>
        </CCardHeader>
        <CCardBody class="p-4">
          <!-- Document Display -->
          <div v-if="obj.documentId && obj.document">
            <div class="form-group mb-4">
              <label class="font-weight-bold">Document preview</label>

              <!-- PDF Display -->
              <div v-if="isPdf(obj.document)" class="border rounded">
                <iframe
                  :src="getDocumentUrl()"
                  :title="getDocumentName()"
                  width="100%"
                  height="600"
                  style="border: none;"
                ></iframe>
              </div>

              <!-- Image Display -->
              <div v-else class="border rounded">
                <CImg
                  :src="getDocumentUrl()"
                  :alt="getDocumentName()"
                  width="100%"
                  height="600"
                  responsive
                  style="object-fit: contain;"
                />
              </div>

              <!-- Document Actions -->
              <div class="mt-3">
                <a
                  :href="getDocumentUrl()"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary mr-3"
                >
                  <CIcon name="cil-external-link" class="mr-1" />
                  Open in new tab
                </a>
                <span class="text-muted">
                  <CIcon name="cil-description" class="mr-1" />
                  {{ getDocumentName() }}
                </span>
              </div>
            </div>
          </div>

          <!-- Upload New Document -->
          <CRow form>
            <CCol md="6">
              <div class="form-group">
                <label class="font-weight-bold">
                  {{ obj.documentId ? "Replace document" : "Upload document" }}
                </label>
                <WidgetsUploadDocument @uploaded="uploaded" />
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <!-- Form Actions -->
      <CCard class="mb-4 border-0">
        <CCardFooter class="bg-transparent border-0 p-4">
          <div class="d-flex justify-content-between">
            <CButton color="light" :disabled="loading" @click="cancel">
              Cancel
            </CButton>
            <CButton color="primary" type="submit" :disabled="loading">
              <CIcon name="cil-check-circle" />
              {{ loading ? "Saving…" : obj.id ? "Save" : "Record quotation" }}
            </CButton>
          </div>
        </CCardFooter>
      </CCard>
    </CForm>

    <!-- Add Vendor Modal (shared) -->
    <VendorFormModal
      :show="addVendorFormPopup"
      :vendor="itemAddNewVendor"
      @close="addVendorFormPopup = false"
      @save="confirmAddVendor"
    />
  </div>
</template>

<script>
import PurchaseQuotationApi from "@/lib/purchaseQuotationApi";
import VendorApi from "@/lib/vendorApi";
import WidgetsUploadDocument from "../widgets/WidgetsUploadDocument.vue";
import VendorFormModal from "../components/VendorFormModal";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mytToday, asDateString, toApiDate } from "@/utils/datetime";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";

export default {
  name: "PurchaseQuotation",
  components: {
    WidgetsUploadDocument,
    VendorFormModal,
    vSelect,
  },
  data() {
    return {
      loading: false,
      uploadedFiles: [],
      orderDate: null,
      obj: {
        date: null,
        orderNo: "",
        vendorId: "",
        validUntil: "",
        description: "",
      },
      submitted: false,
      api: new PurchaseQuotationApi(),
      vendorApi: new VendorApi(),
      vendorItems: [],
      selectedSupplier: null,

      itemAddNewVendor: {},
      addVendorFormPopup: false,
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "Purchase quotations"]);
    this.initializeDefaultDate();
    this.refreshVendor();
    this.resetObj();
  },
  computed: {
    computeDate() {
      return asDateString(this.orderDate);
    },

    supplierItemsWithAddNew() {
      return [...this.vendorItems, { id: "add_new", name: "+ Add new vendor" }];
    },
  },
  methods: {
    badgeColor,
    badgeLabel,

    initializeDefaultDate() {
      this.orderDate = mytToday();
      this.obj.date = this.orderDate;
    },

    refreshVendor() {
      this.vendorApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.vendorItems = response.result || [];
          if (this.obj.vendorId && !this.selectedSupplier) {
            this.selectedSupplier = this.vendorItems.find((v) => v.id === this.obj.vendorId) || null;
          }
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't load your vendors. ${helper.getErrorMessage(data, "Refresh the page to try again.")}`);
        });
    },

    handleSupplierSelect(selectedSupplier) {
      if (selectedSupplier && selectedSupplier.id === "add_new") {
        this.addNewSupplier();
        return;
      }

      this.selectedSupplier = selectedSupplier;
      this.obj.vendorId = selectedSupplier ? selectedSupplier.id : "";
    },

    addNewSupplier() {
      this.itemAddNewVendor = {};
      this.addVendorFormPopup = true;
      this.selectedSupplier = null;
    },

    isPdf(document) {
      try {
        return document.contentType == "application/pdf";
      } catch (error) {
        return false;
      }
    },

    getDocumentUrl() {
      return apiUrl + "documents/file/" + this.obj.documentId;
    },

    getDocumentName() {
      if (this.obj.document == null) return "No document uploaded";
      if (this.obj.document.fileName == null) return "Document";
      return this.obj.document.fileName;
    },

    uploaded(data) {
      this.uploadedFiles = data.uploadedFiles;
      if (this.uploadedFiles && this.uploadedFiles.length > 0) {
        this.obj.documentId = this.uploadedFiles[0].id;
        this.$toast.success("Document uploaded");
      }
    },

    setDate(e) {
      this.orderDate = e;
    },

    resetObj() {
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.orderDate = asDateString(this.obj.date);

            // Set the selected supplier based on the obj.vendorId
            if (this.obj.vendorId) {
              this.selectedSupplier =
                this.vendorItems.find((vendor) => vendor.id === this.obj.vendorId) ||
                this.obj.vendor ||
                null;
            }
          })
          .catch(({ data } = {}) => {
            this.$toast.error(`Couldn't load this vendor quotation. ${helper.getErrorMessage(data, "Go back to the list and try again.")}`);
          });
      } else {
        this.obj = this.getEmptyObj();
        this.selectedSupplier = null;
      }
    },

    validateForm() {
      this.submitted = true;
      const firstInvalid = !this.obj.orderNo
        ? "orderNoField"
        : !this.computeDate
        ? "dateField"
        : !this.obj.vendorId
        ? "vendorField"
        : null;
      if (firstInvalid) {
        this.$nextTick(() => this.focusField(firstInvalid));
        return false;
      }
      return true;
    },

    focusField(refName) {
      const ref = this.$refs[refName];
      const el = ref && (ref.$el || ref);
      if (!el || !el.querySelector) return;
      el.scrollIntoView({ block: "center" });
      const input = el.querySelector("input, textarea, select");
      if (input) input.focus();
    },

    submit() {
      if (!this.validateForm()) {
        return;
      }

      this.onSubmit();
    },

    onSubmit() {
      if (this.loading) return;
      this.loading = true;
      // Date-only field goes to the API as "yyyy-MM-dd" (a Date would shift 8 hours in JSON)
      this.obj.date = toApiDate(this.computeDate) || null;
      const isEdit = !!this.obj.id;

      const apiCall = isEdit ? this.api.update(this.obj) : this.api.create(this.obj);

      apiCall
        .then(() => {
          this.$toast.success(isEdit ? "Vendor quotation saved" : "Vendor quotation recorded");
          this.$router.push({ path: "/tenants/purchaseQuotationList" });
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't save the vendor quotation. ${helper.getErrorMessage(data, "Please try again.")}`);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    cancel() {
      this.$router.push({ path: "/tenants/purchaseQuotationList" });
    },

    getEmptyObj() {
      return {
        orderNo: "",
        date: null,
        vendorId: "",
        validUntil: "",
        description: "",
      };
    },

    confirmAddVendor(vendor) {
      this.vendorApi
        .create(vendor)
        .then((response) => {
          const addedVendor = response.result;
          this.refreshVendor();
          this.selectedSupplier = addedVendor;
          this.obj.vendorId = addedVendor.id;
          this.$toast.success(addedVendor.name ? `${addedVendor.name} added` : "Vendor added");
          this.addVendorFormPopup = false;
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't add the vendor. ${helper.getErrorMessage(data, "Please try again.")}`);
        });
    },

    // Start a purchase order pre-filled from this quotation (PurchaseOrder.vue reads these query params)
    createPurchaseOrder() {
      this.$router.push({
        path: "/tenants/PurchaseOrder",
        query: {
          fromQuotation: this.obj.id,
          vendorId: this.obj.vendorId,
          quotationRef: this.obj.orderNo,
          description: `PO created from vendor quotation ${this.obj.orderNo}`,
        },
      });
    },
  },
};
</script>
